import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  Alert,
  Box,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  HairlineCard,
  IconButton,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  TextField,
  Typography,
  iconSize,
  statusTone,
  useRowActionsColumn,
} from '@munifocus/ui';
import {
  INSPECTION_CATEGORIES,
  inspectionTemplateProblems,
  type InspectionChecklistItem,
  type InspectionTemplateView,
  type SaveInspectionTemplateDto,
} from '@munifocus/contracts-gov';
import { useMutationToast } from '../../lib/mutation-toast';
import { formatDate } from '../../lib/relative-time';
import { useInspectionTemplates, useSaveInspectionTemplate } from '../../lib/permits-api';

type Draft = SaveInspectionTemplateDto & { id?: string };

/** A slug from an English label, unique among `taken`. */
export function itemKeyFor(label: string, taken: ReadonlySet<string>): string {
  const base =
    label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^[^a-z]+/, '')
      .slice(0, 48) || 'item';
  let key = base;
  let n = 2;
  while (taken.has(key)) {
    key = `${base}_${n}`;
    n += 1;
  }
  return key;
}

/**
 * The municipality's inspection checklists. Editing one changes future
 * inspections only: an inspection keeps the checklist it was scheduled with.
 */
export function ChecklistTemplatesPanel({ lg }: { lg: string }) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const templates = useInspectionTemplates(lg);
  const save = useSaveInspectionTemplate(lg);
  const actionsColumn = useRowActionsColumn();
  const [draft, setDraft] = useState<Draft | null>(null);
  const fr = i18n.language.startsWith('fr');

  const edit = (tpl?: InspectionTemplateView) =>
    setDraft(
      tpl
        ? {
            id: tpl.id,
            category: tpl.category,
            nameEn: tpl.nameEn,
            nameFr: tpl.nameFr,
            items: tpl.items,
            isActive: tpl.isActive,
          }
        : { category: 'rental_housing', nameEn: '', nameFr: '', items: [], isActive: true },
    );

  const patchItem = (i: number, p: Partial<InspectionChecklistItem>) =>
    setDraft((d) =>
      d ? { ...d, items: d.items.map((it, j) => (j === i ? { ...it, ...p } : it)) } : d,
    );
  const move = (i: number, by: number) =>
    setDraft((d) => {
      if (!d) return d;
      const items = [...d.items];
      const j = i + by;
      if (j < 0 || j >= items.length) return d;
      [items[i], items[j]] = [items[j], items[i]];
      return { ...d, items };
    });

  const problems = draft
    ? [
        ...inspectionTemplateProblems(draft.items),
        ...(draft.nameEn.trim() && draft.nameFr.trim() ? [] : ['name']),
      ]
    : [];

  return (
    <>
      <DataTable
        rows={templates.data ?? []}
        getRowKey={(r) => r.id}
        isLoading={templates.isLoading}
        emptyLabel={t('gov.inspections.templates.empty')}
        onRowClick={(r) => edit(r)}
        action={
          <ActionButton
            label={t('gov.inspections.templates.new')}
            icon={Icons.Plus}
            onClick={() => edit()}
          />
        }
        columns={[
          {
            key: 'name',
            label: t('gov.inspections.templates.name'),
            primary: true,
            sortValue: (r) => (fr ? r.nameFr : r.nameEn),
            cell: (r) => (
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fr ? r.nameFr : r.nameEn}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`gov.inspections.categories.${r.category}`)} ·{' '}
                  {t('gov.inspections.templates.itemCount', { count: r.items.length })}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'updated',
            label: t('gov.inspections.templates.updated'),
            sortValue: (r) => r.updatedAt,
            cell: (r) => formatDate(r.updatedAt, i18n.language),
          },
          {
            key: 'active',
            label: t('gov.permits.fields.status'),
            sortValue: (r) => (r.isActive ? 0 : 1),
            cell: (r) => (
              <StatusChip
                status={statusTone.enabled[r.isActive ? 'active' : 'inactive']}
                label={t(r.isActive ? 'gov.permits.types.active' : 'gov.permits.types.inactive')}
              />
            ),
          },
          {
            ...actionsColumn,
            cell: (r) => (
              <RowActions>
                <RowAction
                  label={t('gov.permits.types.edit')}
                  icon={Icons.Pencil}
                  onClick={() => edit(r)}
                />
              </RowActions>
            ),
          },
        ]}
      />

      <Dialog open={Boolean(draft)} onClose={() => setDraft(null)} fullWidth maxWidth="md">
        {draft && (
          <>
            <DialogTitle>
              {draft.id
                ? t('gov.inspections.templates.editTitle')
                : t('gov.inspections.templates.new')}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('gov.inspections.templates.nameEn')}
                    value={draft.nameEn}
                    onChange={(e) => setDraft({ ...draft, nameEn: e.target.value })}
                    required
                    fullWidth
                  />
                  <TextField
                    label={t('gov.inspections.templates.nameFr')}
                    value={draft.nameFr}
                    onChange={(e) => setDraft({ ...draft, nameFr: e.target.value })}
                    required
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    select
                    label={t('gov.inspections.templates.category')}
                    value={draft.category}
                    onChange={(e) =>
                      setDraft({ ...draft, category: e.target.value as Draft['category'] })
                    }
                    fullWidth
                  >
                    {INSPECTION_CATEGORIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {t(`gov.inspections.categories.${c}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={draft.isActive}
                        onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                      />
                    }
                    label={t('gov.inspections.templates.inUse')}
                  />
                </Stack>
                <Typography variant="subtitle2">{t('gov.inspections.templates.items')}</Typography>
                {draft.items.map((item, i) => (
                  <HairlineCard key={item.key} sx={{ p: 1.5 }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          {i + 1} · {item.key}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={t('gov.inspections.templates.moveUp')}
                          onClick={() => move(i, -1)}
                          disabled={i === 0}
                        >
                          <Icons.ArrowUp size={iconSize.sm} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={t('gov.inspections.templates.moveDown')}
                          onClick={() => move(i, 1)}
                          disabled={i === draft.items.length - 1}
                        >
                          <Icons.ArrowDown size={iconSize.sm} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={t('gov.inspections.templates.remove')}
                          onClick={() =>
                            setDraft({ ...draft, items: draft.items.filter((_, j) => j !== i) })
                          }
                        >
                          <Icons.Trash2 size={iconSize.sm} />
                        </IconButton>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <TextField
                          size="small"
                          label={t('gov.inspections.templates.sectionEn')}
                          value={item.sectionEn ?? ''}
                          onChange={(e) => patchItem(i, { sectionEn: e.target.value })}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label={t('gov.inspections.templates.sectionFr')}
                          value={item.sectionFr ?? ''}
                          onChange={(e) => patchItem(i, { sectionFr: e.target.value })}
                          fullWidth
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <TextField
                          size="small"
                          label={t('gov.inspections.templates.labelEn')}
                          value={item.labelEn}
                          onChange={(e) => patchItem(i, { labelEn: e.target.value })}
                          required
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label={t('gov.inspections.templates.labelFr')}
                          value={item.labelFr}
                          onChange={(e) => patchItem(i, { labelFr: e.target.value })}
                          required
                          fullWidth
                        />
                      </Stack>
                    </Stack>
                  </HairlineCard>
                ))}
                <Box>
                  <Button
                    startIcon={<Icons.Plus size={iconSize.sm} />}
                    onClick={() => {
                      const taken = new Set(draft.items.map((x) => x.key));
                      const previous = draft.items[draft.items.length - 1];
                      setDraft({
                        ...draft,
                        items: [
                          ...draft.items,
                          {
                            key: itemKeyFor(`item ${draft.items.length + 1}`, taken),
                            labelEn: '',
                            labelFr: '',
                            sectionEn: previous?.sectionEn,
                            sectionFr: previous?.sectionFr,
                          },
                        ],
                      });
                    }}
                  >
                    {t('gov.inspections.templates.addItem')}
                  </Button>
                </Box>
                {problems.length > 0 && (
                  <Alert severity="warning">{t('gov.inspections.templates.incomplete')}</Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDraft(null)}>{t('common.actions.cancel')}</Button>
              <Button
                variant="contained"
                disabled={save.isPending || problems.length > 0}
                onClick={async () => {
                  try {
                    await save.mutateAsync(draft);
                    toast.success('gov.inspections.templates.saved');
                    setDraft(null);
                  } catch (err) {
                    toast.error(err, 'gov.admin.errors.actionFailed');
                  }
                }}
              >
                {t('common.actions.save')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
